/**
 * What the account corner of a public header should offer. One rule for every public header
 * (landing page, sign-in / sign-up pages, sign-up steps, public job and company pages):
 * a signed-in visitor is never shown "Sign in" / "Create account".
 *
 * Pure (moved out of src/components/HeaderAccountActions.tsx on 2026-10-09 so
 * scripts/qa/offline-experience-signup.mjs can check it without React or Firebase).
 */
export type HeaderAccountState =
  | { kind: 'signedOut' }
  /** Auth or the first profile snapshot is still loading: show nothing rather than guess. */
  | { kind: 'resolving' }
  /** Signed in, but sign-up is not finished (no role yet, or the account step is not done). */
  | { kind: 'needsSignup'; finishTo: string }
  | { kind: 'signedIn'; homeTo: string };

/** Pages that ARE the sign-up step: a "Finish sign-up" link there would point at the page itself. */
export const SIGNUP_PATHS = ['/sso/role-selection', '/register/step1', '/employer-registration', '/institute-registration', '/signup'];

export function getHeaderAccountState(input: {
  user: unknown;
  profile:
    | { role?: unknown; orgId?: unknown; onboarding?: { employer?: { completed?: unknown }; emt?: { completed?: unknown } } }
    | null
    | undefined;
  loading: boolean;
  profileReady: boolean;
  probablySignedIn: boolean;
  /**
   * True on the sign-up steps after Register (EmtSignupShell). Only a signed-in account can be on
   * them, so while auth is still restoring the session the header stays neutral even without the
   * localStorage hint (it is missing in a new tab, after storage was cleared, or in private mode).
   * It used to flash "Sign in / Create account" at people who had just created their account.
   */
  onSignupStep?: boolean;
}): HeaderAccountState {
  const { user, profile, loading, profileReady, probablySignedIn, onSignupStep = false } = input;
  if (!user) return loading && (probablySignedIn || onSignupStep) ? { kind: 'resolving' } : { kind: 'signedOut' };
  if (loading || !profileReady) return { kind: 'resolving' };
  const role = typeof profile?.role === 'string' ? profile.role : null;
  // No profile, or a profile without a role: they never picked EMT / employer.
  if (!role) return { kind: 'needsSignup', finishTo: '/sso/role-selection' };
  if (role === 'emt') {
    // Same test as AuthContext's isEmtOnboardingComplete.
    return profile?.onboarding?.emt?.completed === true
      ? { kind: 'signedIn', homeTo: '/dashboard' }
      : { kind: 'needsSignup', finishTo: '/register/step1' };
  }
  if (role === 'employer') {
    const done = profile?.onboarding?.employer?.completed === true || !!profile?.orgId;
    return done
      ? { kind: 'signedIn', homeTo: '/employer/dashboard' }
      : { kind: 'needsSignup', finishTo: '/employer-registration' };
  }
  if (role === 'institute') return { kind: 'signedIn', homeTo: '/institute/dashboard' };
  return { kind: 'needsSignup', finishTo: '/sso/role-selection' };
}
